import * as Dfns from 'date-fns';
import { ReactElement, ReactNode } from 'react';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { MdCheck, MdDelete, MdInfoOutline } from 'react-icons/md';
import { RiPencilFill } from 'react-icons/ri';
import {
  BillingAccount,
  IncludeInFeeCalc,
  InvoiceRow,
  isTVADAccount,
  isYouTubeAccount,
  ManualEntry,
  ManualEntryType,
  TVADBillingAccount,
  SourceBillingAccount,
  YouTubeBillingAccount
} from '@blisspointmedia/bpm-types/dist/BillingAutomation';
import { getBillingMetrics } from '@blisspointmedia/bpm-types/dist/Columns';
import { attachBillingSegmentArrays } from '@blisspointmedia/bpm-types/dist/SegmentationMapping';
import { Checkbox } from '@Components/FormCheck';
import { Appearance, Design, Size } from '@Components/designConstants';
import { DropdownFilterPill } from '@Components/Dropdown/dropdown-constants';
import { IconButton } from '@Components/IconButton';
import { MetricDropdownOption } from '@Components/MetricDropdown/MetricDropdown';
import {
  CustomModalEditWidgetHeader,
  TableRow
} from '@Components/ModalEditWidget/modal-edit-constants';
import { NumberFormatter } from '@Components/NumberFormatter/NumberFormatter';
import { MONTH_DATE_LABEL } from '@src/utils/date-utils';
import {
  awaitJSON,
  fetchCometSelfInvoke,
  SegmentationMappingLambdaFetch
} from '../../utils/fetch-utils';
import { BillingCorrectionTooltip } from './BillingCorrectionTooltip';
import { SegmentRow } from '../SegmentationMapping/segmentation-mapping-constants';

interface ThreeLineOptionProps {
  label: string;
  subLabel: string;
  tertiaryLabel?: string;
}

export const ThreeLineOption = ({
  label,
  subLabel,
  tertiaryLabel
}: ThreeLineOptionProps): ReactNode => (
  <div className='three-line-option'>
    <span className='three-line-option-name'>{label}</span>
    <span className='three-line-option-sub'>{subLabel}</span>
    {tertiaryLabel && <span className='three-line-option-tertiary'>{tertiaryLabel}</span>}
  </div>
);

export interface AccountOption {
  value: string;
  label: string;
  account: BillingAccount | TVADBillingAccount | YouTubeBillingAccount;
}

export const CONFIG_ACCOUNT_CATEGORY = {
  TVAD: 'tvad',
  YOUTUBE: 'youtube',
  OTHER: 'other'
} as const;
export type ConfigAccountCategory =
  (typeof CONFIG_ACCOUNT_CATEGORY)[keyof typeof CONFIG_ACCOUNT_CATEGORY];

export const getConfigAccountCategory = (
  account: BillingAccount | TVADBillingAccount | YouTubeBillingAccount
): ConfigAccountCategory => {
  if (isTVADAccount(account)) {
    return CONFIG_ACCOUNT_CATEGORY.TVAD;
  }
  if (isYouTubeAccount(account)) {
    return CONFIG_ACCOUNT_CATEGORY.YOUTUBE;
  }
  return CONFIG_ACCOUNT_CATEGORY.OTHER;
};

export const splitAccountsByCategory = (
  accounts: (BillingAccount | TVADBillingAccount | YouTubeBillingAccount)[]
): Record<
  ConfigAccountCategory,
  (BillingAccount | TVADBillingAccount | YouTubeBillingAccount)[]
> => {
  const result: Record<
    ConfigAccountCategory,
    (BillingAccount | TVADBillingAccount | YouTubeBillingAccount)[]
  > = {
    [CONFIG_ACCOUNT_CATEGORY.TVAD]: [],
    [CONFIG_ACCOUNT_CATEGORY.YOUTUBE]: [],
    [CONFIG_ACCOUNT_CATEGORY.OTHER]: []
  };
  for (const account of accounts) {
    result[getConfigAccountCategory(account)].push(account);
  }
  return result;
};

export const BILLING_METRIC_LABELS: Record<string, string> = Object.fromEntries(
  getBillingMetrics().map(m => [m.name, m.prettyName])
);

export const CURRENCY_BILLING_METRICS = new Set(['spend', 'revenue', 'cpc', 'cpx']);

export const BILLING_JOB_DAG = 'billing-automation';
export const BILLING_JOB_TASK = 'BILLING-AUTOMATION';
export const BILLING_JOB_TASK_NAME = 'BILLING-AUTOMATION';

export const SpendTypes = {
  MEDIA_PLUS_FEES: 'mediaPlusFees',
  FEES_ONLY: 'feesOnly'
} as const;
export type SpendType = (typeof SpendTypes)[keyof typeof SpendTypes];

// Order here drives the order of SPEND_TYPE_OPTIONS in the Spend Type pane.
export const SPEND_TYPE_LABELS: Record<SpendType, string> = {
  [SpendTypes.FEES_ONLY]: 'Agency Fees Only',
  [SpendTypes.MEDIA_PLUS_FEES]: 'Media Cost + Agency Fees'
};

const SPEND_TYPE_DESCRIPTIONS: Record<SpendType, string> = {
  [SpendTypes.FEES_ONLY]: 'Select this option if the client pays for platform media spend directly',
  [SpendTypes.MEDIA_PLUS_FEES]:
    'Select this option if Tinuiti needs to be reimbursed for platform media spend'
};

export const DspOwners = {
  TINUITI: 'tinuiti',
  CLIENT: 'client'
} as const;
export type DspOwnerType = (typeof DspOwners)[keyof typeof DspOwners];

export const DSP_OWNER_LABELS: Record<DspOwnerType, string> = {
  [DspOwners.TINUITI]: 'Tinuiti Owned',
  [DspOwners.CLIENT]: 'Client Owned'
};

// Object.entries widens keys to string, so cast back to the SpendType union.
export const SPEND_TYPE_OPTIONS: MetricDropdownOption[] = (
  Object.entries(SPEND_TYPE_LABELS) as [SpendType, string][]
).map(([value, label]) => ({
  value,
  label,
  description: SPEND_TYPE_DESCRIPTIONS[value]
}));

export const BILLING_METRIC_OPTIONS: MetricDropdownOption[] = Object.entries(
  BILLING_METRIC_LABELS
).map(([value, label]) => ({
  value,
  label,
  ...(value === 'spend' && {
    customValuePane: { title: 'Spend Type', options: SPEND_TYPE_OPTIONS }
  })
}));

export const DSP_SEAT_OPTIONS: MetricDropdownOption[] = Object.entries(DSP_OWNER_LABELS).map(
  ([value, label]) => ({ value, label })
);

export const accountLabel = (
  acct: BillingAccount | TVADBillingAccount | YouTubeBillingAccount | SourceBillingAccount
): string => {
  const platforms = [...new Set(acct.channelPlatforms.map(cp => cp.platform))].join(', ');
  const channel = isYouTubeAccount(acct) ? 'YouTube - ' : '';
  return `${channel}${platforms} — ${acct.name}`;
};

// Shared TVAD/YouTube/Non-TVAD section labels, grouping helper, and filter pills — kept in one
// place so every dropdown that groups by TVAD/YouTube-ness (AccountSelect's account list,
// AddInvoiceRowModal's channel list) shows the same terminology rather than risking independent
// drift. YouTube gets its own section (rather than folding into TVAD or Non-TVAD) so it isn't
// ambiguously associated with either grouping in these menus.
export const SECTION_TVAD = 'TVAD';
export const SECTION_YOUTUBE = 'YouTube';
export const SECTION_NON_TVAD = 'Non TVAD';

export const getSection = (isYoutube: boolean, isTvad: boolean): string => {
  if (isYoutube) {
    return SECTION_YOUTUBE;
  }
  return isTvad ? SECTION_TVAD : SECTION_NON_TVAD;
};

export const TVAD_FILTER_PILLS: DropdownFilterPill<string>[] = [
  { label: 'All' },
  { label: 'TVAD Only', filter: i => i.section === SECTION_TVAD },
  { label: 'YouTube Only', filter: i => i.section === SECTION_YOUTUBE },
  { label: 'Not TVAD', filter: i => i.section === SECTION_NON_TVAD }
];

export const MONTH_OPTION_LOOKBACK = 12;

export const MONTH_KEY_FORMAT = 'yyyy-MM';

export const invoiceMonthKey = (row: { reportYear: number; reportMonth: number }): string =>
  Dfns.format(new Date(row.reportYear, row.reportMonth - 1), MONTH_KEY_FORMAT);

export const invoiceMonthLabel = (key: string): string => {
  const parsed = Dfns.parse(key, MONTH_KEY_FORMAT, new Date());
  return Dfns.isValid(parsed) ? Dfns.format(parsed, MONTH_DATE_LABEL) : '';
};

/**
 * Builds `count` sequential { label, value } month options, one per calendar month, counting
 * back from `anchor` ("yyyy-MM", defaults to the current month). `value` is "yyyy-MM"; `label`
 * uses `labelFormat` (defaults to MONTH_DATE_LABEL). `startOffset` months are skipped before the
 * first option -- pass 1 to exclude anchor's own month (e.g. "prior months only" pickers).
 * Returns [] if `anchor` is given but isn't a valid "yyyy-MM" string.
 */
export const recentMonthOptions = (
  count: number,
  {
    anchor,
    startOffset = 0,
    labelFormat = MONTH_DATE_LABEL
  }: { anchor?: string; startOffset?: number; labelFormat?: string } = {}
): { value: string; label: string }[] => {
  const anchorDate = anchor ? Dfns.parse(anchor, MONTH_KEY_FORMAT, new Date()) : new Date();
  if (!Dfns.isValid(anchorDate)) {
    return [];
  }
  return Array.from({ length: count }, (_, i) => {
    const date = Dfns.subMonths(anchorDate, startOffset + i);
    return { label: Dfns.format(date, labelFormat), value: Dfns.format(date, MONTH_KEY_FORMAT) };
  });
};

export const invoiceSignedOffLabel = (row: InvoiceRow): string | undefined =>
  row.signedOffAt === null
    ? undefined
    : `Signed off by ${row.signedOffBy ?? 'unknown'} on ${Dfns.format(
        Dfns.parseISO(row.signedOffAt),
        'MMM d, yyyy h:mm a'
      )}`;

export const fetchBillingSegmentCoverage = async (company: string): Promise<SegmentRow[]> => {
  // tinuititest has no real platform data; SegmentationMapping short-circuits it the same way.
  if (company === 'tinuititest') {
    return [];
  }
  let rows: SegmentRow[];
  if (import.meta.env.DEV) {
    const res = await SegmentationMappingLambdaFetch('/getRawPlatformData', {
      params: { company, enableBillingSegments: true }
    });
    rows = await awaitJSON<SegmentRow[]>(res);
  } else {
    // GET: comet self-invoke route
    rows = await fetchCometSelfInvoke<
      { company: string; enableBillingSegments: boolean },
      SegmentRow[]
    >(
      SegmentationMappingLambdaFetch,
      '/getRawPlatformData',
      { company, enableBillingSegments: true },
      { method: 'GET', noTimeout: true }
    );
  }
  return attachBillingSegmentArrays(rows);
};

const EMPTY_DASH = <span className='empty-display'>—</span>;

export const textCell = (value: string | null) =>
  value ? <span className='invoice-cell-text'>{value}</span> : EMPTY_DASH;

const FEE_CALC_ICONS: Record<string, ReactNode> = {
  [IncludeInFeeCalc.YES]: <MdCheck className='invoice-bool-check' />,
  [IncludeInFeeCalc.NO]: EMPTY_DASH
};

const renderFeeCalcCell = (row: InvoiceRow) => (
  <OverlayTrigger
    placement='top'
    overlay={<Tooltip id={`fee-calc-${row.calculationId}`}>{row.includeInFeeCalc}</Tooltip>}
  >
    <span>{FEE_CALC_ICONS[row.includeInFeeCalc]}</span>
  </OverlayTrigger>
);

export const EXPORT_LOCKED_MESSAGE =
  'This invoice has been exported to finance and can no longer be modified.';

export const SELF_SIGN_OFF_LOCKED_MESSAGE =
  "You last edited this manual entry, so you can't also sign off on it. Ask another team member to review and sign off.";

interface InvoiceHeaderOptions {
  onToggleSignOff: (row: InvoiceRow) => void;
  onEditRow: (row: TableRow<InvoiceRow>) => void;
  onDeleteRow: (row: TableRow<InvoiceRow>) => void;
  userEmail: string;
}

export const isEditableManualInvoice = (row: InvoiceRow): boolean =>
  row.manual != null && row.signedOffAt === null && !row.exported;

export const MANUAL_ENTRY_TYPE_TO_PRETTY_NAME: Record<ManualEntryType, string> = {
  [ManualEntry.CORRECTION]: 'Correction',
  [ManualEntry.OUT_OF_APP]: 'Out-of-App Spend'
};

const manualBadge = (row: InvoiceRow): ReactElement | null => {
  if (row.manual == null) {
    return null;
  }
  const prettyName = MANUAL_ENTRY_TYPE_TO_PRETTY_NAME[row.manual.entryType];
  const forMonth = row.manual.correctionMonth
    ? ` for ${invoiceMonthLabel(row.manual.correctionMonth)}`
    : '';
  const tip = `${prettyName}${forMonth}: ${row.manual.note}`;
  return (
    <OverlayTrigger
      placement='top'
      overlay={<Tooltip id={`manual-${row.calculationId}`}>{tip}</Tooltip>}
    >
      <span className='manual-badge'>
        <MdInfoOutline />
      </span>
    </OverlayTrigger>
  );
};

export const invoiceRowsToExportData = (rows: InvoiceRow[]): Record<string, string | number>[] =>
  rows.map(row => ({
    Month: invoiceMonthLabel(invoiceMonthKey(row)),
    Account: row.accountName ?? '',
    'Salesforce Relationship': row.salesforceRelationshipName ?? '',
    'Billing Category': row.billingCategory ?? '',
    Channel: row.channel,
    Platform: row.platform,
    'Metric to Bill': row.metricToBill,
    Billable: row.billable ? 'Yes' : 'No',
    'Include in Fee Calc': row.includeInFeeCalc,
    'Signed Off By': row.signedOffBy ?? '',
    'Signed Off At': row.signedOffAt ?? ''
  }));

export const getInvoiceHeaders = ({
  onToggleSignOff,
  onEditRow,
  onDeleteRow,
  userEmail
}: InvoiceHeaderOptions): CustomModalEditWidgetHeader[] => [
  {
    label: '',
    field: 'calculationId',
    flex: 0,
    width: 40,
    uneditable: true,
    hideInPendingChanges: true,
    renderer: (row: InvoiceRow) => <div className='billing-cell-centered'>{manualBadge(row)}</div>
  },
  {
    label: 'Month',
    field: 'reportYear',
    flex: 0.5,
    minFlexWidth: 110,
    contentGetter: (row: InvoiceRow) => invoiceMonthKey(row),
    renderer: (row: InvoiceRow) => (
      <div className='billing-cell-centered'>{invoiceMonthLabel(invoiceMonthKey(row))}</div>
    )
  },
  {
    label: 'Account',
    field: 'accountName',
    flex: 2,
    minFlexWidth: 160,
    renderer: (row: InvoiceRow) => (
      <div className='billing-cell-centered'>{textCell(row.accountName)}</div>
    )
  },
  {
    label: 'Salesforce Relationship',
    field: 'salesforceRelationshipName',
    flex: 2,
    minFlexWidth: 160,
    renderer: (row: InvoiceRow) => (
      <div className='billing-cell-centered'>{textCell(row.salesforceRelationshipName)}</div>
    )
  },
  {
    label: 'Billing Category',
    field: 'billingCategory',
    flex: 1,
    minFlexWidth: 80,
    renderer: (row: InvoiceRow) => (
      <div className='billing-cell-centered'>{textCell(row.billingCategory)}</div>
    )
  },
  {
    label: 'Channel',
    field: 'channel',
    flex: 1,
    minFlexWidth: 100,
    renderer: (row: InvoiceRow) => <div className='billing-cell-centered'>{row.channel}</div>
  },
  {
    label: 'Platform',
    field: 'platform',
    flex: 1,
    minFlexWidth: 100,
    renderer: (row: InvoiceRow) => <div className='billing-cell-centered'>{row.platform}</div>
  },
  {
    label: 'Metric to Bill',
    field: 'metricToBill',
    flex: 0.5,
    minFlexWidth: 110,
    renderer: (row: InvoiceRow) => (
      <div className='billing-cell-centered'>
        <NumberFormatter
          value={row.metricToBill}
          type={
            row.manual != null ||
            (row.billingMetric && CURRENCY_BILLING_METRICS.has(row.billingMetric))
              ? '$'
              : '#2'
          }
          decimals={2}
        />
      </div>
    )
  },
  {
    label: 'Billable',
    field: 'billable',
    flex: 0.5,
    minFlexWidth: 90,
    renderer: (row: InvoiceRow) => (
      <div className='billing-cell-centered'>
        {row.billable ? <MdCheck className='invoice-bool-check' /> : EMPTY_DASH}
      </div>
    )
  },
  {
    label: 'Include in Fee Calc',
    field: 'includeInFeeCalc',
    flex: 0.5,
    minFlexWidth: 110,
    renderer: (row: InvoiceRow) => (
      <div className='billing-cell-centered'>{renderFeeCalcCell(row)}</div>
    )
  },
  {
    label: 'Signed Off',
    field: 'signedOffAt',
    flex: 0.5,
    minFlexWidth: 90,
    nonInteractive: true,
    uneditable: true,
    renderer: (row: InvoiceRow) => {
      const isSelfSignOffLocked =
        row.signedOffAt === null && row.manual?.lastUpdatedBy === userEmail;
      const checkbox = (
        <Checkbox
          className='invoice-signoff-checkbox'
          checked={row.signedOffAt !== null}
          disabled={row.exported === true || isSelfSignOffLocked}
          onChange={() => onToggleSignOff(row)}
        />
      );
      const signedOffLabel = invoiceSignedOffLabel(row);

      if (signedOffLabel === undefined && !isSelfSignOffLocked) {
        return <div className='billing-cell-centered'>{checkbox}</div>;
      }

      if (signedOffLabel && !row.exported) {
        return (
          <div className='billing-cell-centered'>
            <BillingCorrectionTooltip id={`signoff-${row.calculationId}`} message={signedOffLabel}>
              {checkbox}
            </BillingCorrectionTooltip>
          </div>
        );
      }

      // Exported/self-sign-off-locked lines have a disabled checkbox, so wrap it in a span for the
      // tooltip to fire — disabled elements don't emit the pointer events OverlayTrigger needs.
      if (row.exported) {
        return (
          <div className='billing-cell-centered'>
            <BillingCorrectionTooltip
              id={`signoff-locked-${row.calculationId}`}
              message={EXPORT_LOCKED_MESSAGE}
            >
              <span>{checkbox}</span>
            </BillingCorrectionTooltip>
          </div>
        );
      }

      return (
        <div className='billing-cell-centered'>
          <OverlayTrigger
            placement='top'
            overlay={
              <Tooltip id={`signoff-locked-${row.calculationId}`}>
                {SELF_SIGN_OFF_LOCKED_MESSAGE}
              </Tooltip>
            }
          >
            <span>{checkbox}</span>
          </OverlayTrigger>
        </div>
      );
    }
  },
  {
    label: '',
    field: 'calculationId',
    flex: 0,
    width: 64,
    uneditable: true,
    hideInPendingChanges: true,
    renderer: (row: TableRow<InvoiceRow>) =>
      isEditableManualInvoice(row) ? (
        <div className='invoice-action-buttons'>
          <IconButton
            design={Design.BRAND}
            appearance={Appearance.EMPTY}
            size={Size.SM}
            onClick={e => {
              e.stopPropagation();
              onEditRow(row);
            }}
          >
            <RiPencilFill />
          </IconButton>
          <IconButton
            design={Design.BRAND}
            appearance={Appearance.EMPTY}
            size={Size.SM}
            onClick={e => {
              e.stopPropagation();
              onDeleteRow(row);
            }}
          >
            <MdDelete />
          </IconButton>
        </div>
      ) : (
        EMPTY_DASH
      )
  }
];
